/**
 * Design System Provider
 *
 * Wrapper component that injects the design system CSS globally.
 * This component:
 * 1. Imports the design system CSS variables and component classes
 * 2. Imports global styles for MUI component enhancements
 * 3. Sets up CSS variables for use throughout the application
 *
 * This should wrap the entire application (along with ThemeProvider) in index.js.
 *
 * Provider Hierarchy (in index.js):
 * <DesignSystemProvider>
 *   <AppProvider>
 *     <ThemeProvider theme={theme}>
 *       <App />
 *     </ThemeProvider>
 *   </AppProvider>
 * </DesignSystemProvider>
 *
 * Usage:
 * <DesignSystemProvider>
 *   <App />
 * </DesignSystemProvider>
 */

import React, { useEffect, useState } from 'react';
import '../styles/design-system.css';
import '../styles/global-styles.css';
import '../styles/glassmorphic-nav.css';

export function DesignSystemProvider({ children, theme: customTheme = null }) {
  const [isLoaded, setIsLoaded] = useState(false);

  // Verify design system CSS is loaded
  useEffect(() => {
    const checkDSLoaded = () => {
      const element = document.documentElement;
      const style = window.getComputedStyle(element);
      const tealColor = style.getPropertyValue('--teal-600');

      if (tealColor && tealColor.trim().length > 0) {
        setIsLoaded(true);
      } else {
        // Retry after a short delay if not loaded yet
        setTimeout(checkDSLoaded, 50);
      }
    };

    checkDSLoaded();
  }, []);

  // Optional: Log warning if design system fails to load
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!isLoaded) {
        console.warn('Design System CSS may not have loaded. Check that design-system.css is properly imported.');
      }
    }, 2000);

    return () => clearTimeout(timeout);
  }, [isLoaded]);

  return <>{children}</>;
}

export default DesignSystemProvider;
